import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  DocumentSectionCitation,
  TranscriptChunk,
} from '@/shared/api/transcriptor';
import { Modal } from '@/shared/ui';

import { i18n } from '@/features/consultationOptions';

import { formatTimecode } from '../../lib';

import styles from './CitationPopup.module.scss';

type CitationPopupProps = {
  className?: string;
  isOpen: boolean;
  title: string;
  citations: DocumentSectionCitation[];
  transcriptChunks: TranscriptChunk[];
  onClose: () => void;
};

export const CitationPopup: FC<CitationPopupProps> = (props) => {
  const { isOpen, title, citations, transcriptChunks, onClose } = props;

  const { formatMessage } = useIntl();

  const citedChunks = useMemo(() => {
    if (!citations.length || !transcriptChunks.length) {
      return [];
    }

    return citations
      .flatMap((citation) => {
        // Match by timecode range
        if (
          citation.timecodeStart !== undefined &&
          citation.timecodeStart !== null &&
          citation.timecodeEnd !== undefined &&
          citation.timecodeEnd !== null
        ) {
          return transcriptChunks.filter(
            (chunk) =>
              chunk.start !== undefined &&
              chunk.end !== undefined &&
              chunk.start >= (citation.timecodeStart ?? 0) &&
              chunk.end <= (citation.timecodeEnd ?? 0),
          );
        }

        // Match by index range
        if (
          citation.start !== undefined &&
          citation.start !== null &&
          citation.end !== undefined &&
          citation.end !== null
        ) {
          return transcriptChunks.slice(citation.start, citation.end + 1);
        }

        // Single index match
        if (citation.start !== undefined && citation.start !== null) {
          const chunk = transcriptChunks[citation.start];
          return chunk ? [chunk] : [];
        }

        return [];
      })
      .filter(
        (chunk, index, self) =>
          self.findIndex(
            (c) => c.start === chunk.start && c.text === chunk.text,
          ) === index,
      );
  }, [citations, transcriptChunks]);

  return (
    <Modal
      data-component="citation-popup"
      isOpen={isOpen}
      title={`${formatMessage(i18n.consultationOptionsMessages.citationsTitle)}: ${title}`}
      onCancel={onClose}
      hideFooter
      shouldRenderCloseIconButton
    >
      <div className={styles.container}>
        {citedChunks.length > 0 ? (
          citedChunks.map((chunk, index) => (
            <div key={index} className={styles.citation}>
              {chunk.start !== undefined && (
                <span className={styles.timestamp}>
                  {formatTimecode(chunk.start)}
                </span>
              )}
              <p className={styles.citationText}>{chunk.text}</p>
            </div>
          ))
        ) : (
          <p className={styles.empty}>
            {formatMessage(i18n.consultationOptionsMessages.citationsTitle)}
          </p>
        )}
      </div>
    </Modal>
  );
};
